import { useEffect, useRef, useState } from "react";
import { Divider, Menu, MenuItem } from "@mui/material";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { authSelector, logoutUser } from "../redux/slices/authSlice";
import adminAvatar from "../portals/admin/assets/admin-user.svg";
import styles from "./AdminTopbar.module.css";

export const ADMIN_DISPLAY_NAME = "U-Send Admin";

export const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=31642793793";
export const HELP_URL = "/help/";

export const Svg = ({ children, className }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

export const ChatIcon = () => (
  <Svg>
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" />
  </Svg>
);
export const HelpIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </Svg>
);
export const CardIcon = () => (
  <Svg>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </Svg>
);
export const LogoutIcon = () => (
  <Svg>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </Svg>
);

// Shows a hairline under the bar once its scroll container (the page card) scrolls.
export function useScrolled(ref) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const container = ref.current?.parentElement;
    if (!container) return undefined;
    const onScroll = () => setScrolled(container.scrollTop > 0);
    onScroll();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [ref]);
  return scrolled;
}

export default function AdminTopbar({ onMenu }) {
  const dispatch = useDispatch();
  const { loggedUser } = useSelector(authSelector);
  const [anchorEl, setAnchorEl] = useState(null);
  const barRef = useRef(null);
  const scrolled = useScrolled(barRef);

  const user = loggedUser?.user || {};
  // Shared team account: one display name, whoever is logged in.
  const name = ADMIN_DISPLAY_NAME;
  const close = () => setAnchorEl(null);

  return (
    <header ref={barRef} className={`${styles.bar} ${scrolled ? styles.scrolled : ""}`}>
      {onMenu && (
        <button type="button" className={`${styles.iconButton} ${styles.menuButton}`} onClick={onMenu} aria-label="Open menu">
          <Svg>
            <path d="M4 7h16" />
            <path d="M4 12h16" />
            <path d="M4 17h16" />
          </Svg>
        </button>
      )}
      <a className={styles.iconButton} href={WHATSAPP_URL} target="_blank" rel="noreferrer" title="WhatsApp an agent" aria-label="WhatsApp an agent">
        <ChatIcon />
      </a>
      <a className={styles.iconButton} href={HELP_URL} target="_blank" rel="noreferrer" title="Help centre" aria-label="Help centre">
        <HelpIcon />
      </a>
      <span className={styles.divider} />
      <button
        type="button"
        className={styles.account}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {/* Same avatar as the sidebar account row. */}
        <img className={styles.avatar} src={adminAvatar} alt="" />
        <span className={styles.text}>
          <span className={styles.name}>{name}</span>
          {user.email && <span className={styles.email}>{user.email}</span>}
        </span>
        <Svg className={styles.chevron}>
          <path d="m6 9 6 6 6-6" />
        </Svg>
      </button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 240, mt: 0.75 } }}
      >
        <div className={styles.menuHeader}>
          <span className={styles.name}>{name}</span>
          <span className={styles.email}>{user.email || "Not available"}</span>
          <span className={styles.role}>Admin</span>
        </div>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem component={Link} to="/admin/payments" onClick={close}>
          <span className={styles.menuItem}>
            <CardIcon />
            Payments
          </span>
        </MenuItem>
        <MenuItem component="a" href={WHATSAPP_URL} target="_blank" rel="noreferrer" onClick={close}>
          <span className={styles.menuItem}>
            <ChatIcon />
            Contact an agent
          </span>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => dispatch(logoutUser())}>
          <span className={`${styles.menuItem} ${styles.danger}`}>
            <LogoutIcon />
            Logout
          </span>
        </MenuItem>
      </Menu>
    </header>
  );
}
